import React, { useEffect, useState } from "react";
import {
    tokenUsageTracker,
    RefillSettings as RefillSettingsType,
} from "../services/tokenUsageTracker";
import TokenUsageTracker from "../services/tokenUsageTracker";
import { useToast } from "../contexts/ToastContext";
import { apiService } from "../services/api";

interface RefillSettingsProps {
    className?: string;
}

export const RefillSettings: React.FC<RefillSettingsProps> = ({
    className = "",
}) => {
    const [settings, setSettings] = useState<RefillSettingsType>(
        tokenUsageTracker.getRefillSettings()
    );
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(
        settings.millionsPerRefill.toString()
    );
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const { showToast } = useToast();

    // Load subscription metadata on mount
    useEffect(() => {
        const loadSubscriptionSettings = async () => {
            try {
                const subscriptionData = await apiService.subscriptions.getSubscriptionStatus();
                
                // Check if we have subscription metadata
                if (subscriptionData.usage_data?.stripeSubscription?.metadata) {
                    const metadata = subscriptionData.usage_data.stripeSubscription.metadata;
                    
                    // Update settings from subscription metadata
                    const loadedSettings: RefillSettingsType = {
                        mode: metadata.refillBehaviour === "auto" ? "auto" : "off",
                        millionsPerRefill: parseInt(metadata.refillBatchQuantity || "2", 10)
                    };
                    
                    setSettings(loadedSettings);
                    setEditValue(loadedSettings.millionsPerRefill.toString());
                    
                    // Also update the token usage tracker
                    tokenUsageTracker.updateRefillSettings(loadedSettings);
                }
            } catch (error) {
                console.error("Failed to load subscription settings:", error);
                // Use local settings as fallback
            } finally {
                setIsLoading(false);
            }
        };

        loadSubscriptionSettings();
    }, []);

    useEffect(() => {
        const handleSettingsUpdate = (newSettings: RefillSettingsType) => {
            setSettings(newSettings);
            setEditValue(newSettings.millionsPerRefill.toString());
        };

        tokenUsageTracker.on("refillSettingsUpdated", handleSettingsUpdate);

        return () => {
            tokenUsageTracker.off(
                "refillSettingsUpdated",
                handleSettingsUpdate
            );
        };
    }, []);

    const handleModeToggle = () => {
        const newMode = settings.mode === "off" ? "auto" : "off";
        setSettings({ ...settings, mode: newMode });
    };

    const handleAmountChange = (delta: number) => {
        const newAmount = Math.max(
            1,
            Math.min(10, settings.millionsPerRefill + delta)
        );
        setSettings({ ...settings, millionsPerRefill: newAmount });
        setEditValue(newAmount.toString());
    };

    const handleEditStart = () => {
        // Allow editing regardless of auto-refill status
        setIsEditing(true);
        setEditValue(settings.millionsPerRefill.toString());
    };

    const handleEditEnd = () => {
        setIsEditing(false);
        const value = parseInt(editValue, 10);
        if (!isNaN(value) && value >= 1 && value <= 10000) {
            setSettings({ ...settings, millionsPerRefill: value });
        } else {
            setEditValue(settings.millionsPerRefill.toString());
        }
    };

    const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value === "" || /^\d+$/.test(value)) {
            setEditValue(value);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);

        try {
            // Update subscription metadata in Stripe
            await apiService.subscriptions.updateRefillSettings(
                settings.mode,
                settings.millionsPerRefill
            );

            // Update the local token usage tracker with new settings
            tokenUsageTracker.updateRefillSettings(settings);

            showToast({
                message: "Refill settings saved successfully",
                type: "success",
            });
        } catch (error: any) {
            console.error("Failed to save refill settings:", error);
            
            // Check if it's a no subscription error
            if (error.response?.data?.detail?.includes("No active subscription")) {
                showToast({
                    message: "You need an active subscription to configure refill settings",
                    type: "error",
                });
            } else {
                showToast({
                    message: error.response?.data?.detail || "Failed to save refill settings",
                    type: "error",
                });
            }
        } finally {
            setIsSaving(false);
        }
    };

    // Calculate refill cost based on current settings (not editValue)
    const refillCost = `$${(
        (settings.millionsPerRefill *
            TokenUsageTracker.PRICE_PER_MILLION_CENTS) /
        100
    ).toFixed(2)}`;
    const isAutoEnabled = settings.mode === "auto";

    return (
        <div
            className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 relative ${className}`}
        >
            {/* Loading overlay */}
            {(isSaving || isLoading) && (
                <div className="absolute inset-0 bg-white bg-opacity-75 rounded-lg flex items-center justify-center z-10">
                    <div className="flex items-center gap-3">
                        <svg
                            className="animate-spin h-5 w-5 text-blue-600"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            ></circle>
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                        <span className="text-gray-600">{isLoading ? "Loading..." : "Saving..."}</span>
                    </div>
                </div>
            )}

            <h3 className="text-lg font-medium text-gray-900 mb-4">
                Refill Settings
            </h3>

            <div className="space-y-6">
                {/* Auto-refill toggle */}
                <div className="flex items-center justify-between">
                    <div>
                        <label
                            htmlFor="auto-refill"
                            className="text-sm font-medium text-gray-900"
                        >
                            Auto-refill
                        </label>
                        <p className="text-xs text-gray-500 mt-1">
                            Automatically purchase tokens when your balance gets low
                        </p>
                    </div>
                    <button
                        id="auto-refill"
                        type="button"
                        onClick={handleModeToggle}
                        disabled={isSaving}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                            isAutoEnabled ? "bg-blue-600" : "bg-gray-200"
                        } ${isSaving ? "opacity-50 cursor-not-allowed" : ""}`}
                        role="switch"
                        aria-checked={isAutoEnabled}
                    >
                        <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                isAutoEnabled
                                    ? "translate-x-6"
                                    : "translate-x-1"
                            }`}
                        />
                    </button>
                </div>

                {/* Refill amount */}
                <div
                    className={`space-y-3 ${
                        !isAutoEnabled || isSaving ? "opacity-50" : ""
                    }`}
                >
                    <label className="text-sm font-medium text-gray-900">
                        Tokens per refill
                    </label>

                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => handleAmountChange(-1)}
                            disabled={
                                settings.millionsPerRefill <= 1 || isSaving
                            }
                            className={`p-2 rounded-md border ${
                                settings.millionsPerRefill > 1 && !isSaving
                                    ? "border-gray-300 hover:bg-gray-50 text-gray-700"
                                    : "border-gray-200 text-gray-400 cursor-not-allowed"
                            }`}
                        >
                            <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M20 12H4"
                                />
                            </svg>
                        </button>

                        <div className="flex-1 text-center">
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={editValue}
                                    onChange={handleEditChange}
                                    onBlur={handleEditEnd}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") handleEditEnd();
                                        if (e.key === "Escape") {
                                            setEditValue(
                                                settings.millionsPerRefill.toString()
                                            );
                                            setIsEditing(false);
                                        }
                                    }}
                                    className="w-20 text-2xl font-semibold text-center text-gray-900 border-b-2 border-blue-500 focus:outline-none"
                                    autoFocus
                                    disabled={isSaving}
                                />
                            ) : (
                                <div
                                    onClick={handleEditStart}
                                    className={`text-2xl font-semibold text-gray-900 ${
                                        !isSaving
                                            ? "cursor-text hover:text-blue-600"
                                            : ""
                                    }`}
                                    title={!isSaving ? "Click to edit" : ""}
                                >
                                    {settings.millionsPerRefill}M
                                </div>
                            )}
                            <div className="text-sm text-gray-500">tokens</div>
                        </div>

                        <button
                            onClick={() => handleAmountChange(1)}
                            disabled={
                                settings.millionsPerRefill >= 10 || isSaving
                            }
                            className={`p-2 rounded-md border ${
                                settings.millionsPerRefill < 10 && !isSaving
                                    ? "border-gray-300 hover:bg-gray-50 text-gray-700"
                                    : "border-gray-200 text-gray-400 cursor-not-allowed"
                            }`}
                        >
                            <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 4v16m8-8H4"
                                />
                            </svg>
                        </button>
                    </div>

                    <div className="bg-gray-50 rounded-md p-3">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">
                                Cost per refill
                            </span>
                            <span className="text-lg font-semibold text-gray-900">
                                {refillCost}
                            </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            $
                            {(
                                TokenUsageTracker.PRICE_PER_MILLION_CENTS / 100
                            ).toFixed(2)}{" "}
                            per million tokens
                        </div>
                    </div>
                </div>

                {isAutoEnabled && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                        <div className="flex items-start gap-2">
                            <svg
                                className="w-4 h-4 text-blue-600 mt-0.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                            <div className="text-xs text-blue-800">
                                <p className="font-medium">
                                    Auto-refill is enabled
                                </p>
                                <p className="mt-1">
                                    When your token balance gets low, we'll
                                    automatically purchase{" "}
                                    {settings.millionsPerRefill}M tokens for{" "}
                                    {refillCost}.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Save button */}
                <div className="pt-4 border-t border-gray-200">
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className={`w-full px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${
                            isSaving
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        }`}
                    >
                        {isSaving ? "Saving..." : "Save Settings"}
                    </button>
                </div>
            </div>
        </div>
    );
};
